import React, { useEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Check, Copy, Download, Loader2, Maximize2, Minimize2, X, Trash2, Link2, Search, Folder } from 'lucide-react'
import ConfirmationModal from './ConfirmationModal'

export interface NoteEditorAsset {
  id: string
  title: string
  content?: string
  createdAt?: string
  related_to_content_id?: string | null
  metadata?: any
  isDraft?: boolean // NEW: indicates if note is a draft (not yet saved to backend)
  spaceId?: string | null // Space to save the note to
}

interface Space {
  id: string
  name: string
  color?: string
}

interface ContentItem {
  id: string
  title: string
  url?: string
  category?: string
}

interface NoteEditorModalProps {
  isOpen: boolean
  onClose: () => void
  note: NoteEditorAsset | null
  token: string
  onSaved?: () => void
  onDeleted?: () => void // NEW: callback when note is deleted
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return ''
  try {
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return ''
    return d.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  } catch {
    return ''
  }
}

export default function NoteEditorModal({ isOpen, onClose, note, token, onSaved, onDeleted }: NoteEditorModalProps) {
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [copied, setCopied] = useState(false)
  const [isFullBleed, setIsFullBleed] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  
  // Link to content state
  const [showLinkPicker, setShowLinkPicker] = useState(false)
  const [linkSearch, setLinkSearch] = useState('')
  const [linkResults, setLinkResults] = useState<ContentItem[]>([])
  const [linkLoading, setLinkLoading] = useState(false)
  const [linkedContent, setLinkedContent] = useState<ContentItem | null>(null)
  const linkSearchRef = useRef<HTMLInputElement>(null)
  
  // Space selector state
  const [spaces, setSpaces] = useState<Space[]>([])
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(null)
  const [showSpacePicker, setShowSpacePicker] = useState(false)

  const bodyRef = useRef<HTMLTextAreaElement | null>(null)

  const createdLabel = useMemo(() => {
    return formatDate(note?.createdAt || note?.metadata?.created_at || note?.metadata?.display_date)
  }, [note])

  const attachedLabel = useMemo(() => {
    const sourceTitle = note?.metadata?.source_title
    const sourceUrl = note?.metadata?.source_url
    const relatedId = note?.related_to_content_id || note?.metadata?.source_content_id
    
    // If linked to saved content, show that
    if (relatedId) {
      return { type: 'linked', text: sourceTitle || 'View content', id: relatedId }
    }
    // If has source URL but not linked (standalone), show the URL
    if (sourceUrl) {
      return { type: 'url', text: sourceTitle || sourceUrl, url: sourceUrl }
    }
    return null
  }, [note])

  useEffect(() => {
    if (!note) return
    setTitle((note.title || '').replace(/^Note:\s*/i, '') || 'Untitled note')
    setBody(note.content || '')
    setSaveStatus('idle')
    setCopied(false)
    setIsSaving(false)
    setShowLinkPicker(false)
    setLinkSearch('')
    setLinkedContent(null)
    setSelectedSpaceId(note.spaceId || null)
    setShowSpacePicker(false)
    // focus body for fast capture
    setTimeout(() => bodyRef.current?.focus(), 50)
  }, [note, isOpen])

  // Fetch spaces for draft notes
  useEffect(() => {
    if (!isOpen || !token || !note?.isDraft) return
    
    const fetchSpaces = async () => {
      try {
        const res = await fetch('/api/spaces', {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (res.ok) {
          const data = await res.json()
          setSpaces(data.spaces || [])
        }
      } catch (err) {
        console.error('Failed to fetch spaces:', err)
      }
    }
    
    fetchSpaces()
  }, [isOpen, token, note?.isDraft])

  // Search for content to link - only when user types
  useEffect(() => {
    if (!showLinkPicker || !token || !linkSearch.trim()) {
      setLinkResults([])
      return
    }
    
    const searchContent = async () => {
      setLinkLoading(true)
      try {
        const res = await fetch(`/api/content?search=${encodeURIComponent(linkSearch)}&limit=5`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (res.ok) {
          const data = await res.json()
          // Filter out notes and screenshots, only show articles/videos
          const filtered = (data.data || []).filter((item: any) => 
            !item.tags?.includes('note') && 
            !item.tags?.includes('screenshot') &&
            item.metadata?.type !== 'note' &&
            item.metadata?.type !== 'screenshot'
          )
          setLinkResults(filtered.slice(0, 5))
        }
      } catch (err) {
        console.error('Failed to search content:', err)
      } finally {
        setLinkLoading(false)
      }
    }
    
    const debounce = setTimeout(searchContent, 300)
    return () => clearTimeout(debounce)
  }, [linkSearch, showLinkPicker, token])

  useEffect(() => {
    if (!isOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [isOpen])

  if (!isOpen || !note) return null

  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${title}\n\n${body}`.trim())
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // ignore
    }
  }

  const doDownload = () => {
    const blob = new Blob([`# ${title}\n\n${body}`], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${title || 'note'}.md`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const doSave = async () => {
    if (!token) return

    setIsSaving(true)
    setSaveStatus('idle')

    try {
      // If it's a draft, create it in backend. Otherwise, update existing.
      if (note?.isDraft) {
        // For new notes, combine title and body as the note text
        const noteText = `${title}\n\n${body}`.trim() || 'New note'
        
        // Build request body - only include related_to_content_id if it has a value
        const requestBody: any = { text: noteText }
        if (linkedContent?.id) {
          requestBody.related_to_content_id = linkedContent.id
        }
        
        const res = await fetch('/api/content/notes', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(requestBody)
        })

        if (!res.ok) throw new Error('save failed')
        
        const data = await res.json()
        const newNoteId = data.data?.asset?.id

        // If a space is selected, add the note to that space
        if (selectedSpaceId && newNoteId) {
          await fetch(`/api/spaces/${selectedSpaceId}/items`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({ contentIds: [newNoteId] })
          })
        }

        setSaveStatus('success')
        setTimeout(() => {
          setSaveStatus('idle')
          onSaved?.()
          onClose()
        }, 1500)
      } else {
        // For existing notes, update title and content separately
        const res = await fetch(`/api/content/${note.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            title: `Note: ${title || 'Untitled note'}`,
            content: body,
            related_to_content_id: linkedContent?.id || note.related_to_content_id || null
          })
        })

        if (!res.ok) throw new Error('save failed')

        setSaveStatus('success')
        setTimeout(() => setSaveStatus('idle'), 1500)
        onSaved?.()
      }
    } catch {
      setSaveStatus('error')
      setTimeout(() => setSaveStatus('idle'), 2500)
    } finally {
      setIsSaving(false)
    }
  }

  const handleDeleteClick = () => {
    if (!token || !note || note.isDraft) {
      // If it's a draft, just close without saving
      onClose()
      return
    }
    setShowDeleteConfirm(true)
    setDeleteError(null)
  }

  const doDelete = async () => {
    if (!token || !note) return

    try {
      const res = await fetch(`/api/content/${note.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      if (!res.ok) throw new Error('delete failed')

      setShowDeleteConfirm(false)
      onDeleted?.()
      onClose()
    } catch (err) {
      console.error('Failed to delete note:', err)
      setDeleteError('Failed to delete note. Please try again.')
    }
  }

  return (
    <AnimatePresence>
      <motion.div
        key="note-editor-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-6"
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

        {/* Sheet */}
        <motion.div
          key="note-editor-sheet"
          initial={{ y: 16, opacity: 0, scale: 0.99 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 16, opacity: 0, scale: 0.99 }}
          transition={{ type: 'spring', damping: 26, stiffness: 320 }}
          className={
            isFullBleed
              ? 'relative w-full h-full bg-white rounded-2xl shadow-2xl overflow-hidden'
              : 'relative w-full max-w-[980px] h-[min(720px,calc(100vh-2rem))] bg-white rounded-2xl shadow-2xl overflow-hidden'
          }
          onClick={(e) => e.stopPropagation()}
        >
          {/* Top bar (minimal) */}
          <div className="flex flex-col gap-2 px-5 py-3 border-b border-gray-200/70">
            {/* Source link - Top (show linked content or existing attachment) */}
            {(linkedContent || attachedLabel || (note?.isDraft && selectedSpaceId)) && (
              <div className="text-sm text-gray-900 flex items-center gap-4 flex-wrap">
                {/* Space indicator for drafts */}
                {note?.isDraft && selectedSpaceId && (
                  <span>
                    <span className="font-medium">Space:</span>{' '}
                    <span className="text-teal-600 font-medium">{spaces.find(s => s.id === selectedSpaceId)?.name}</span>
                  </span>
                )}
                {/* Linked content indicator */}
                {(linkedContent || attachedLabel) && (
                  <span>
                    <span className="font-medium">Linked to:</span>{' '}
                    {linkedContent ? (
                      <span className="text-blue-600 font-medium">{linkedContent.title}</span>
                    ) : attachedLabel?.type === 'linked' ? (
                      <button
                        onClick={() => {
                          if (attachedLabel.id) {
                            window.location.href = `/library?highlight=${attachedLabel.id}`;
                          }
                        }}
                        className="text-blue-500 hover:text-blue-600 hover:underline font-medium"
                      >
                        {attachedLabel.text}
                      </button>
                    ) : (
                      <a
                        href={attachedLabel?.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:text-blue-600 hover:underline font-medium"
                        title={attachedLabel?.url}
                      >
                        {(attachedLabel?.text?.length || 0) > 60 ? attachedLabel?.text?.slice(0, 60) + '...' : attachedLabel?.text}
                      </a>
                    )}
                  </span>
                )}
              </div>
            )}
            
            {/* Title Row */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-2 w-2 rounded-full bg-amber-400" />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">Note</div>
                  {createdLabel && (
                    <div className="text-xs text-gray-500 truncate">{createdLabel}</div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-1">
              {/* Save Button - First position */}
              <button
                onClick={doSave}
                disabled={isSaving}
                className="px-4 py-1.5 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>

              {/* Link to Content Button */}
              <div className="relative">
                <button
                  onClick={() => {
                    setShowLinkPicker(!showLinkPicker)
                    setTimeout(() => linkSearchRef.current?.focus(), 100)
                  }}
                  className={`p-2 rounded-lg transition ${
                    linkedContent || attachedLabel 
                      ? 'text-blue-600 bg-blue-50 hover:bg-blue-100' 
                      : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                  }`}
                  title={linkedContent ? `Linked to: ${linkedContent.title}` : 'Link to content'}
                >
                  <Link2 className="w-4 h-4" />
                </button>
                
                {/* Link Picker Dropdown */}
                {showLinkPicker && (
                  <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
                    <div className="p-3 border-b border-gray-100">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          ref={linkSearchRef}
                          type="text"
                          value={linkSearch}
                          onChange={(e) => setLinkSearch(e.target.value)}
                          placeholder="Search content to link..."
                          className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    {linkSearch.trim() && (
                      <div className="max-h-64 overflow-y-auto">
                        {linkLoading ? (
                          <div className="p-4 text-center text-gray-500 text-sm">
                            <Loader2 className="w-4 h-4 animate-spin mx-auto mb-1" />
                            Searching...
                          </div>
                        ) : linkResults.length === 0 ? (
                          <div className="p-4 text-center text-gray-500 text-sm">
                            No content found
                          </div>
                        ) : (
                          linkResults.map((item) => (
                            <button
                              key={item.id}
                              onClick={() => {
                                setLinkedContent(item)
                                setShowLinkPicker(false)
                              }}
                              className="w-full px-4 py-3 text-left hover:bg-gray-50 transition flex items-start gap-3 border-b border-gray-50 last:border-0"
                            >
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900 truncate">{item.title}</div>
                                {item.category && (
                                  <div className="text-xs text-gray-500 mt-0.5">{item.category}</div>
                                )}
                              </div>
                              {linkedContent?.id === item.id && (
                                <Check className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                    {linkedContent && (
                      <div className="p-2 border-t border-gray-100 bg-gray-50">
                        <button
                          onClick={() => {
                            setLinkedContent(null)
                            setShowLinkPicker(false)
                          }}
                          className="w-full px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition"
                        >
                          Remove link
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Space Picker Button - Only for draft notes */}
              {note?.isDraft && (
                <div className="relative">
                  <button
                    onClick={() => setShowSpacePicker(!showSpacePicker)}
                    className={`p-2 rounded-lg transition ${
                      selectedSpaceId 
                        ? 'text-teal-600 bg-teal-50 hover:bg-teal-100' 
                        : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                    }`}
                    title={selectedSpaceId ? `Save to: ${spaces.find(s => s.id === selectedSpaceId)?.name}` : 'Save to space'}
                  >
                    <Folder className="w-4 h-4" />
                  </button>
                  
                  {/* Space Picker Dropdown */}
                  {showSpacePicker && (
                    <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
                      <div className="p-3 border-b border-gray-100">
                        <div className="text-sm font-medium text-gray-700">Save to Space</div>
                        <div className="text-xs text-gray-500 mt-0.5">Choose where to save this note</div>
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {spaces.length === 0 ? (
                          <div className="p-4 text-center text-gray-500 text-sm">
                            No spaces yet
                          </div>
                        ) : (
                          <>
                            {/* No space option */}
                            <button
                              onClick={() => {
                                setSelectedSpaceId(null)
                                setShowSpacePicker(false)
                              }}
                              className="w-full px-4 py-3 text-left hover:bg-gray-50 transition flex items-center gap-3 border-b border-gray-50"
                            >
                              <div className="w-3 h-3 rounded-full bg-gray-300" />
                              <span className="text-sm text-gray-600">No space (Library only)</span>
                              {!selectedSpaceId && (
                                <Check className="w-4 h-4 text-blue-600 ml-auto" />
                              )}
                            </button>
                            {spaces.map((space) => (
                              <button
                                key={space.id}
                                onClick={() => {
                                  setSelectedSpaceId(space.id)
                                  setShowSpacePicker(false)
                                }}
                                className="w-full px-4 py-3 text-left hover:bg-gray-50 transition flex items-center gap-3 border-b border-gray-50 last:border-0"
                              >
                                <div className={`w-3 h-3 rounded-full ${
                                  space.color === 'blue' ? 'bg-blue-400' :
                                  space.color === 'green' ? 'bg-teal-400' :
                                  space.color === 'orange' ? 'bg-amber-400' :
                                  space.color === 'red' ? 'bg-red-400' :
                                  space.color === 'pink' ? 'bg-pink-400' :
                                  space.color === 'yellow' ? 'bg-yellow-400' :
                                  'bg-blue-400'
                                }`} />
                                <span className="text-sm font-medium text-gray-900">{space.name}</span>
                                {selectedSpaceId === space.id && (
                                  <Check className="w-4 h-4 text-blue-600 ml-auto" />
                                )}
                              </button>
                            ))}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Copy Icon */}
              <button
                onClick={doCopy}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
                title="Copy to clipboard"
              >
                {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              </button>

              {/* Download Icon */}
              <button
                onClick={doDownload}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
                title="Download as markdown"
              >
                <Download className="w-4 h-4" />
              </button>

              {/* Delete Icon - gray default, red on hover */}
              <button
                onClick={handleDeleteClick}
                className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition"
                title={note.isDraft ? 'Discard draft' : 'Delete note'}
              >
                <Trash2 className="w-4 h-4" />
              </button>

              {/* Fullscreen Icon */}
              <button
                onClick={() => setIsFullBleed((v) => !v)}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
                title="Toggle fullscreen"
              >
                {isFullBleed ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>

              {/* Close Icon */}
              <button
                onClick={onClose}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            </div>
          </div>

          {/* Content area */}
          <div className="h-full overflow-auto">
            <div className="px-8 py-8 md:px-12 md:py-10">
              {/* Title */}
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Untitled note"
                className="w-full text-4xl md:text-5xl font-semibold tracking-tight text-gray-900 placeholder:text-gray-300 bg-transparent outline-none"
              />

              {/* Body */}
              <div className="mt-6">
                <textarea
                  ref={bodyRef}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Write something…"
                  className="w-full min-h-[420px] text-[18px] leading-7 text-gray-800 placeholder:text-gray-300 bg-transparent outline-none resize-none"
                />

                {/* Inline save feedback (minimal) */}
                {saveStatus !== 'idle' && (
                  <div
                    className={
                      saveStatus === 'success'
                        ? 'mt-4 text-sm text-emerald-600'
                        : 'mt-4 text-sm text-red-600'
                    }
                  >
                    {saveStatus === 'success' ? 'Saved' : 'Could not save. Try again.'}
                  </div>
                )}
                {deleteError && (
                  <div className="mt-4 text-sm text-red-600">
                    {deleteError}
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false)
          setDeleteError(null)
        }}
        onConfirm={doDelete}
        title="Delete Note"
        message="Are you sure you want to delete this note? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isDestructive={true}
      />
    </AnimatePresence>
  )
}
